import type { UiAction } from '@kanbu/schema/contracts';
import {
  type PropsWithChildren,
  createContext,
  useContext,
  useMemo,
} from 'react';

export interface ChatRendererConfig {
  insert?: (message: string) => void;
  append?: (message: string, options?: { uiAction: UiAction }) => void;
  /**
   * Recovers from a failed or interrupted turn — continues in place or
   * resends the last user message, depending on what's recoverable.
   */
  retry?: () => void | Promise<void>;
  isFetching?: boolean;
}

export const ChatRendererContext = createContext<ChatRendererConfig | null>(
  null,
);

interface ChatRendererProps extends ChatRendererConfig, PropsWithChildren {}

/**
 * Provider for chat renderer, provides options for chat renderer.
 */
export function ChatRendererProvider({
  insert,
  append,
  retry,
  isFetching,
  children,
}: ChatRendererProps) {
  const contextValue = useMemo(
    () => ({ insert, append, retry, isFetching }),
    [insert, append, retry, isFetching],
  );

  return (
    <ChatRendererContext.Provider value={contextValue}>
      {children}
    </ChatRendererContext.Provider>
  );
}

/**
 * Hook for accessing chat renderer context.
 */
export function useChatRenderer() {
  const context = useContext(ChatRendererContext);

  if (!context) {
    throw new Error('Missing ChatConfigContext.Provider in the tree');
  }

  return context;
}
